import { SportUtils } from "@prestosports/prestostats-core/Common";
import { groupBy, sortBy } from "./array-util";
import { getByPath } from "./object-util";

/**
 * Turns the ui schema into the ordered sections and rows the settings form
 * draws (PSTATS-1267).
 *
 * Extracted from Configuration so the layout rule can be tested on its own:
 * core's getSettingDefinitions has to produce the same order for the info
 * dialog and the printed sheet, and the two agreeing is the whole point of
 * that criterion.
 */

// A section's own entries are its fields plus the two strings buildUiSchema
// stamps on it (title, type). Fields carry a `key`; nothing else does.
const isField = (value) =>
	value && typeof value === "object" && value.type !== "object";

// A field another setting switches on and off: form data `visibleWhen`
// ({ key, values }) lists the values of `key` that show it, e.g. flag
// football's First Down Every (yds) and First Down Spots, which share a cell
// and follow the First Down Setting. It may also be a list of such objects,
// shown while any one holds (PSTATS-1268: flag's Overtime Starting Yardline
// stays while Kickoff Play Entry or OT Kickoffs is off, since core treats a
// hidden saved OT Kickoffs as off). Read from the form's current values, so
// the cell swaps as soon as that setting changes. Without values (a caller
// that only wants the layout: the definitions dialog and the printed sheet)
// every field counts as shown.
export const isShown = (field, values) => {
	const { visibleWhen } = field;
	return (
		!visibleWhen ||
		!values ||
		[]
			.concat(visibleWhen)
			.some((condition) =>
				condition.values.includes(getByPath(values, condition.key))
			)
	);
};

export const toSortedRows = (fields, values) => {
	const items = Object.entries(fields)
		.filter(([, value]) => value.row && !value.disabled && isShown(value, values))
		.map(([, value]) => value);

	const rows = groupBy(items, "row");

	Object.entries(rows).forEach(([, v]) => {
		v.sort(sortBy("order"));
	});

	return rows;
};

export const toSortedSections = (uiSchema, sectionOrder, values) => {
	// Each field tagged with the section that DRAWS it, which is the one it is
	// stored in unless its form data names another. The regroup is
	// presentational: no configuration key moves.
	const drawn = {};
	const configTitles = {};

	Object.entries(uiSchema).forEach(([sectionKey, section]) => {
		if (section.type !== "object") return;
		configTitles[sectionKey] = section.title;

		Object.entries(section).forEach(([fieldKey, field]) => {
			if (!isField(field)) return;
			const target = field.section || sectionKey;
			drawn[target] = { ...drawn[target], [fieldKey]: field };
		});
	});

	// Insertion order is config key order, with a section that exists only on
	// screen landing wherever its first field happens to sit - which is an
	// accident, so a declared order wins.
	const appeared = Object.keys(drawn);
	const order = sectionOrder
		? sectionOrder.filter((key) => appeared.includes(key))
		: appeared;

	return order.map((key) => ({
		key,
		// A section with no config key of its own has no title from
		// buildUiSchema either, so it is named the same way every other
		// section is.
		title: configTitles[key] ?? SportUtils.fromCamelCase(key),
		rows: toSortedRows(drawn[key], values)
	}));
};

/**
 * A radio input's value is always a DOM string, but an option's code is typed:
 * firstDownFlagStyle stores -1 or 1, BooleanType stores true or false. Going
 * out through String() and back through the option list is what keeps the
 * stored value's own type - store "-1" where -1 was expected and every
 * equality check the form makes still passes while every numeric one
 * elsewhere quietly stops working (firstDownFlagStyle's visibleWhen matches
 * -1 against the stored code, so "-1" would hide both first-down controls).
 */
export const toRadioValue = (code) => String(code);

export const fromRadioValue = (options, raw) =>
	options.find((option) => toRadioValue(option.code) === raw)?.code;
